import * as Sentry from '@sentry/react'

/**
 * Sentry Replay in buffer mode: the last minute of the session stays in
 * memory, masked, and leaves the browser only when a bug is reported.
 */

/** Start the in-memory buffer, once per page load. */
export function startReplayBuffering(): void {
  const replay = Sentry.getReplay()
  if (!replay || replay.getReplayId()) return
  replay.startBuffering()
}

/**
 * Send the buffered minute to Sentry and return its replay id, or null.
 *
 * Never throws and never waits longer than ``timeoutMs``: a report must go
 * out even when Sentry is slow or unreachable.
 *
 * ``flush({ continueRecording: false })`` only stops the DOM recording: the
 * replay stays enabled on the same session, so ``startBuffering()`` alone
 * would be a no-op and the next report would reuse this id with an empty
 * segment. Once the flush settles the replay is stopped (in buffer mode this
 * sends nothing and clears the session) and buffering restarts on a new one.
 * The restart runs after the flush, not after the timeout: stopping earlier
 * would cut the upload short.
 */
export async function captureReplayId(timeoutMs = 3000): Promise<string | null> {
  const replay = Sentry.getReplay()
  const id = replay?.getReplayId()
  if (!replay || !id) return null
  const flushed = replay.flush({ continueRecording: false })
  void flushed
    .catch(() => {
      // The id is still worth sending: Sentry may have part of the replay.
    })
    .then(async () => {
      await replay.stop()
      replay.startBuffering()
    })
    .catch(() => {
      // Losing the next buffer must not surface as an unhandled rejection.
    })
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    await Promise.race([
      flushed,
      new Promise((resolve) => {
        timer = setTimeout(resolve, timeoutMs)
      }),
    ])
  } catch {
    // Already handled above: the report goes out with the id anyway.
  } finally {
    clearTimeout(timer)
  }
  return id
}
